// src/features/clinical/components/prescription/TestSelector.jsx
import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaFlask } from 'react-icons/fa';
import clinicalApi from '../../services/clinicalApi';

const TestSelector = ({ onTestsChange, initialTests = [] }) => {
  const [tests, setTests] = useState(initialTests);
  const [availableTests, setAvailableTests] = useState([]);

  useEffect(() => {
    loadTests();
  }, []);

  useEffect(() => {
    onTestsChange(tests);
  }, [tests]);

  const loadTests = async () => {
    try {
      const response = await clinicalApi.getTests();
      setAvailableTests(response.data);
    } catch (error) {
      console.error('Failed to load tests:', error);
    }
  };

  const addTest = () => {
    setTests([
      ...tests,
      {
        id: Date.now(),
        testId: '',
        name: '',
        priority: 'ROUTINE',
        instructions: ''
      }
    ]);
  };

  const removeTest = (index) => {
    setTests(tests.filter((_, i) => i !== index));
  };

  const updateTest = (index, field, value) => {
    const updated = [...tests];
    updated[index][field] = value;

    if (field === 'testId') {
      const selectedTest = availableTests.find(t => t.id === value);
      if (selectedTest) {
        updated[index].name = selectedTest.name;
      }
    }

    setTests(updated);
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'URGENT': return 'text-red-600 bg-red-100';
      case 'STAT': return 'text-purple-600 bg-purple-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <FaFlask className="mr-2 text-purple-600" />
          Recommended Tests
        </h3>
        <button
          type="button"
          onClick={addTest}
          className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
        >
          <FaPlus className="mr-2" /> Add Test
        </button>
      </div>

      {tests.map((test, index) => (
        <div key={test.id} className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
          <div className="flex-1 grid grid-cols-3 gap-3">
            <select
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={test.testId}
              onChange={(e) => updateTest(index, 'testId', e.target.value)}
            >
              <option value="">Select Test</option>
              {availableTests.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} - ${t.price}
                </option>
              ))}
            </select>

            <select
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={test.priority}
              onChange={(e) => updateTest(index, 'priority', e.target.value)}
            >
              <option value="ROUTINE">Routine</option>
              <option value="URGENT">Urgent</option>
              <option value="STAT">STAT</option>
            </select>

            <input
              type="text"
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={test.instructions}
              onChange={(e) => updateTest(index, 'instructions', e.target.value)}
              placeholder="Special instructions"
            />
          </div>
          <button
            type="button"
            onClick={() => removeTest(index)}
            className="text-red-600 hover:text-red-800"
          >
            <FaTrash />
          </button>
        </div>
      ))}

      {tests.length > 0 && (
        <div className="mt-2">
          {tests.map((test, index) => (
            test.testId && (
              <span key={index} className={`inline-block px-2 py-1 text-xs rounded-full mr-2 ${getPriorityColor(test.priority)}`}>
                {test.priority}
              </span>
            )
          ))}
        </div>
      )}

      {tests.length === 0 && (
        <p className="text-gray-500 text-center py-4">No tests added. Click "Add Test" to recommend tests.</p>
      )}
    </div>
  );
};

export default TestSelector;